import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Modal, Button } from '../../ui'
import { appsyncQuery } from '../../api/appsync-client.js'
import { usePairs } from '../../pages/tasks/hooks/usePairs.js'
import { CREATE_TASK } from './graphql.js'
import { CreateTaskForm } from './CreateTaskForm.jsx'
import { TasksConfirmDialog } from './TasksConfirmDialog.jsx'

function makeInitialAssignment({ defaultPairId, pairs, currentUserId }) {
  if (!pairs || pairs.length === 0) return null
  // Honour an explicit defaultPairId — pre-select the viewer as the
  // assignee in that pair.
  if (defaultPairId) {
    const pair = pairs.find((p) => p.pairId === defaultPairId)
    if (pair) return { pairId: pair.pairId, assigneeId: currentUserId }
  }
  // Single pair → fix it; default the assignee to the viewer.
  if (pairs.length === 1) {
    return { pairId: pairs[0].pairId, assigneeId: currentUserId }
  }
  // Multiple pairs → user must pick.
  return null
}

function makeInitialDraft({ defaultPairId, defaultTitle, pairs, currentUserId }) {
  return {
    title: defaultTitle ?? '',
    assignment: makeInitialAssignment({ defaultPairId, pairs, currentUserId }),
    priority: 'MEDIUM',
    dueDate: null,
    status: 'TODO',
  }
}

function isDirty(draft, initial) {
  return (
    draft.title !== initial.title ||
    draft.assignment?.pairId !== initial.assignment?.pairId ||
    draft.assignment?.assigneeId !== initial.assignment?.assigneeId ||
    draft.priority !== initial.priority ||
    draft.dueDate !== initial.dueDate ||
    draft.status !== initial.status
  )
}

/**
 * The full task creation modal. Two trigger surfaces (sidebar `+ New
 * task` button and Tasks page header `+ New task` button) share the
 * same modal instance via `App.jsx`.
 *
 * Issue #243 simplified the form: description was removed (it was
 * never displayed in the UI) and the pair selector folded into a
 * single smart assignee selector.
 */
export function CreateTaskModal({
  open,
  onClose,
  onCreated,
  defaultPairId = null,
  defaultTitle = '',
  me,
  reports = [],
}) {
  const pairs = usePairs(me, reports)
  const currentUserId = me?.userId
  const titleRef = useRef(null)

  const initial = useMemo(
    () => makeInitialDraft({ defaultPairId, defaultTitle, pairs, currentUserId }),
    [defaultPairId, defaultTitle, pairs, currentUserId],
  )
  const [draft, setDraft] = useState(initial)
  const [submitting, setSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [titleError, setTitleError] = useState(null)
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false)

  // Reset form state on every fresh open. Do not carry stale state across
  // openings unless `defaultTitle` / `defaultPairId` are provided.
  useEffect(() => {
    if (open) {
      setDraft(initial)
      setErrorMessage(null)
      setTitleError(null)
      setSubmitting(false)
    }
  }, [open, initial])

  const handleChange = useCallback((patch) => {
    setDraft((prev) => ({ ...prev, ...patch }))
    if ('title' in patch && patch.title.trim()) {
      setTitleError(null)
    }
  }, [])

  const requestClose = useCallback(() => {
    if (isDirty(draft, initial)) {
      setConfirmDiscardOpen(true)
    } else {
      onClose()
    }
  }, [draft, initial, onClose])

  const confirmDiscard = useCallback(() => {
    setConfirmDiscardOpen(false)
    onClose()
  }, [onClose])

  const handleSubmit = useCallback(async () => {
    if (!draft.title.trim()) {
      setTitleError('Title is required.')
      return
    }
    if (!draft.assignment?.pairId || !draft.assignment?.assigneeId) {
      setErrorMessage('Pick an assignee for the task.')
      return
    }
    const pair = pairs.find((p) => p.pairId === draft.assignment.pairId)
    if (!pair) {
      setErrorMessage('Selected pair is no longer available.')
      return
    }

    setSubmitting(true)
    setErrorMessage(null)
    try {
      const data = await appsyncQuery(CREATE_TASK, {
        input: {
          reportId: pair.reportId,
          managerId: pair.managerId,
          assignee: draft.assignment.assigneeId,
          title: draft.title.trim(),
          status: draft.status,
          priority: draft.priority,
          dueDate: draft.dueDate || null,
        },
      })
      const created = data?.createTask
      if (onCreated) onCreated(created)
      onClose()
    } catch (err) {
      setErrorMessage(
        err?.message
          ? `Couldn't create task: ${err.message}`
          : "Couldn't create task — please retry",
      )
    } finally {
      setSubmitting(false)
    }
  }, [draft, pairs, onCreated, onClose])

  const noPairs = pairs.length === 0
  const hasAssignment = !!(draft.assignment?.pairId && draft.assignment?.assigneeId)
  const canSubmit = !submitting && !noPairs && draft.title.trim().length > 0 && hasAssignment

  return (
    <>
      <Modal
        open={open}
        onClose={requestClose}
        title="New task"
        width="md"
        initialFocusRef={titleRef}
        // While the discard confirm is layered on top, swallow Escape /
        // backdrop clicks on the parent so the user can interact with the
        // confirm without dismissing both modals at once.
        dismissOnEscape={!confirmDiscardOpen}
        dismissOnBackdrop={!confirmDiscardOpen}
      >
        <CreateTaskForm
          draft={draft}
          onChange={handleChange}
          pairs={pairs}
          currentUserId={currentUserId}
          errorMessage={errorMessage}
          titleError={titleError}
          titleRef={titleRef}
        />
        <div className="modal__actions">
          <Button variant="secondary" onClick={requestClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={submitting}
            loadingLabel="Creating…"
          >
            Create
          </Button>
        </div>
      </Modal>
      <TasksConfirmDialog
        open={confirmDiscardOpen}
        title="Discard this task?"
        message="You have unsaved changes that will be lost."
        confirmLabel="Discard"
        danger
        onCancel={() => setConfirmDiscardOpen(false)}
        onConfirm={confirmDiscard}
      />
    </>
  )
}
